/**
 * Number formatting utilities
 * Matches original game's display formatting
 * Extended for late-game numbers (up to 10^99+)
 */

const SUFFIXES = [
  '',       // 10^0
  'K',      // 10^3 - Thousand
  'M',      // 10^6 - Million
  'B',      // 10^9 - Billion
  'T',      // 10^12 - Trillion
  'Qa',     // 10^15 - Quadrillion
  'Qi',     // 10^18 - Quintillion
  'Sx',     // 10^21 - Sextillion
  'Sp',     // 10^24 - Septillion
  'Oc',     // 10^27 - Octillion
  'No',     // 10^30 - Nonillion
  'Dc',     // 10^33 - Decillion
  'UDc',    // 10^36 - Undecillion
  'DDc',    // 10^39 - Duodecillion
  'TDc',    // 10^42 - Tredecillion
  'QaDc',   // 10^45 - Quattuordecillion
  'QiDc',   // 10^48 - Quindecillion
  'SxDc',   // 10^51 - Sexdecillion
  'SpDc',   // 10^54 - Septendecillion
  'OcDc',   // 10^57 - Octodecillion
  'NoDc',   // 10^60 - Novemdecillion
  'Vg',     // 10^63 - Vigintillion
  'UVg',    // 10^66 - Unvigintillion
  'DVg',    // 10^69 - Duovigintillion
  'TVg',    // 10^72 - Trevigintillion
  'QaVg',   // 10^75 - Quattuorvigintillion
  'QiVg',   // 10^78 - Quinvigintillion
  'SxVg',   // 10^81 - Sexvigintillion
  'SpVg',   // 10^84 - Septenvigintillion
  'OcVg',   // 10^87 - Octovigintillion
  'NoVg',   // 10^90 - Novemvigintillion
  'Tg',     // 10^93 - Trigintillion
  'UTg',    // 10^96 - Untrigintillion
  'DTg',    // 10^99 - Duotrigintillion
];

/**
 * Format a number with appropriate suffix (K, M, B, T, etc.)
 * Falls back to scientific notation for very large numbers
 */
export function formatNumber(num: number, decimals = 0): string {
  if (num === 0) return '0';
  if (!isFinite(num)) return 'Infinity';
  if (isNaN(num)) return 'NaN';

  const isNegative = num < 0;
  num = Math.abs(num);

  // For small numbers, just format with commas
  if (num < 1000) {
    const formatted = decimals > 0
      ? num.toFixed(decimals)
      : Math.floor(num).toString();
    return isNegative ? `-${formatted}` : formatted;
  }

  // Find appropriate suffix
  let suffixIndex = 0;
  let displayNum = num;

  while (displayNum >= 1000 && suffixIndex < SUFFIXES.length - 1) {
    displayNum /= 1000;
    suffixIndex++;
  }

  // If number is still too large for suffixes, use scientific notation
  if (displayNum >= 1000) {
    const exp = Math.floor(Math.log10(num));
    const mantissa = num / Math.pow(10, exp);
    const formatted = `${mantissa.toFixed(2)}e${exp}`;
    return isNegative ? `-${formatted}` : formatted;
  }

  // Format with appropriate precision
  let formatted: string;
  if (displayNum >= 100) {
    formatted = displayNum.toFixed(1);
  } else if (displayNum >= 10) {
    formatted = displayNum.toFixed(2);
  } else {
    formatted = displayNum.toFixed(3);
  }

  // Remove trailing zeros after decimal
  if (formatted.includes('.')) {
    formatted = formatted.replace(/\.?0+$/, '');
  }

  const result = `${formatted}${SUFFIXES[suffixIndex]}`;
  return isNegative ? `-${result}` : result;
}

/**
 * Format a number using scientific notation
 * Useful for displaying very large numbers compactly
 */
export function formatScientific(num: number, precision = 2): string {
  if (num === 0) return '0';
  if (!isFinite(num)) return 'Infinity';

  const isNegative = num < 0;
  num = Math.abs(num);

  const exp = Math.floor(Math.log10(num));
  const mantissa = num / Math.pow(10, exp);

  const formatted = `${mantissa.toFixed(precision)}e${exp}`;
  return isNegative ? `-${formatted}` : formatted;
}

/**
 * Format number with commas (no suffix)
 */
export function formatWithCommas(num: number, decimals = 0): string {
  const fixed = decimals > 0 ? num.toFixed(decimals) : Math.floor(num).toString();
  const parts = fixed.split('.');
  parts[0] = parts[0]!.replace(/\B(?=(\d{3})+(?!\d))/g, ',');
  return parts.join('.');
}

/**
 * Format currency (USD)
 */
export function formatCurrency(amount: number): string {
  if (amount >= 1000000) {
    return `$${formatNumber(amount)}`;
  }

  const isNegative = amount < 0;
  amount = Math.abs(amount);

  const formatted = amount.toFixed(2).replace(/\B(?=(\d{3})+(?!\d))/g, ',');
  return isNegative ? `-$${formatted}` : `$${formatted}`;
}

/**
 * Format percentage
 */
export function formatPercent(value: number, decimals = 1): string {
  return `${(value * 100).toFixed(decimals)}%`;
}

/**
 * Format time duration
 */
export function formatDuration(ms: number): string {
  if (ms < 1000) return `${Math.round(ms)}ms`;

  const seconds = Math.floor(ms / 1000);
  const minutes = Math.floor(seconds / 60);
  const hours = Math.floor(minutes / 60);
  const days = Math.floor(hours / 24);

  if (days > 0) {
    return `${days}d ${hours % 24}h`;
  }
  if (hours > 0) {
    return `${hours}h ${minutes % 60}m`;
  }
  if (minutes > 0) {
    return `${minutes}m ${seconds % 60}s`;
  }
  return `${seconds}s`;
}

/**
 * Parse formatted number back to value
 */
export function parseFormattedNumber(str: string): number {
  str = str.replace(/[,$]/g, '').trim();

  for (let i = SUFFIXES.length - 1; i > 0; i--) {
    if (str.endsWith(SUFFIXES[i]!)) {
      const numPart = parseFloat(str.slice(0, -SUFFIXES[i]!.length));
      return numPart * Math.pow(1000, i);
    }
  }

  return parseFloat(str) || 0;
}
